import dayjs from "dayjs";
import { objectIdString } from "@Helpers/schemas/common.schema";
import type { LayoutData } from "@ServerTypes/portal/portal.schema";
import type { TranslatedString, TranslationKey } from "@i18n";
import type { ExternalLinkListItem, ExternalLinkShareableResourceType } from "./externalLinks.api";
import type { ExternalLinkTarget } from "./CreateExternalLinkModal";

/**
 * The `t` returned by `useTranslation()`. `@i18n` augments i18next's own `TFunction` rather than
 * exporting a reusable alias, so the callable shape is restated here — narrowed to a single key
 * (none of these helpers passes a key array).
 */
type Translate = (key: TranslationKey, options?: Record<string, unknown>) => TranslatedString;

/**
 * Display helpers shared by the three internal external-link surfaces (Manage modal, details panel,
 * Portal Settings register), so a link's expiry/permission/status never reads differently on one
 * screen than another.
 */

/** The grid-wide timestamp format used by SlickGrid1 (`MMM D,YYYY [at] hh:mm:ss A`). */
/**
 * Input limits mirrored from the server's Zod request schema (externalLinks.schema.ts).
 *
 * The server stays the authority — these only stop the user typing something certain to be rejected,
 * so the feedback is an inert control rather than an error toast after submitting.
 */
export const LINK_NAME_MAX_LENGTH = 200;
/** PRD 3.2: "minimum 6 characters". Must stay equal to the server's own floor. */
export const LINK_PASSWORD_MIN_LENGTH = 6;
export const LINK_PASSWORD_MAX_LENGTH = 128;

const GRID_DATE_FORMAT = "MMM D,YYYY [at] hh:mm:ss A";

/** Compact date for columns where the time of day carries no meaning (Created On, Expiry). */
const SHORT_DATE_FORMAT = "MMM D, YYYY";

export function formatGridTimestamp(value: number | undefined): string {
  if (!value) return "";
  return dayjs(value).format(GRID_DATE_FORMAT);
}

export function formatShortDate(value: number | undefined): string {
  if (!value) return "";
  return dayjs(value).format(SHORT_DATE_FORMAT);
}

/**
 * Expiry as shown to INTERNAL users, who are in the portal's own timezone context and for whom
 * a browser-local rendering is correct.
 *
 * Recipients are a different case entirely: FR-37 requires their expiry be shown in the project's
 * timezone with the zone labelled, never converted locally — so the recipient viewer uses the
 * server-formatted `expiresAtLabel` off the resolve response instead of this function.
 */
export function formatExpiryForInternal(t: Translate, expiresAt: number | undefined): string {
  if (!expiresAt) return t("Never");
  return formatShortDate(expiresAt);
}

export function permissionLabel(t: Translate, permission: string): string {
  return permission === "download" ? t("Can Download") : t("Can View");
}

/**
 * Why a link is dead, for the Manage modal's Status column.
 *
 * Order matters: an explicit revoke or admin-disable is the more informative answer even when the
 * link has also since passed its expiry, so those are checked before the date comparison.
 */
export function linkStatusLabel(t: Translate, link: ExternalLinkListItem): string {
  if (link.revoked) return t("Revoked");
  if (link.disabledByAdmin) return t("Disabled by admin");
  if (link.expiresAt && link.expiresAt <= Date.now()) return t("Expired");
  return t("Active");
}

/**
 * Copy a share URL to the clipboard.
 *
 * Falls back to a hidden `<textarea>` + `execCommand` because `navigator.clipboard` is unavailable
 * on insecure origins — which is exactly the on-prem HTTP deployments this product ships to, where
 * the modern API would silently reject and the user would get an empty paste.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path rather than reporting failure: a rejected clipboard
    // permission is recoverable here.
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

/**
 * End-of-day in the BROWSER's timezone for a `<input type="date">` value.
 *
 * DISPLAY ONLY — do not send this to the server. The authoritative expiry instant is resolved from
 * the bare date server-side, in the PORTAL's timezone (`endOfDayInZone`), because a creator whose
 * machine sits in a different zone than the project would otherwise set an expiry the recipient
 * reads as a different day. This exists purely to render the "expires at the end of <date>" hint
 * beside the picker.
 */
export function endOfDayTimestamp(dateValue: string): number | null {
  if (!dateValue) return null;
  const parsed = dayjs(dateValue).endOf("day");
  return parsed.isValid() ? parsed.valueOf() : null;
}

/** Inverse of {@link endOfDayTimestamp}, for populating the picker when editing an existing link. */
export function timestampToDateInput(value: number | undefined): string {
  if (!value) return "";
  return dayjs(value).format("YYYY-MM-DD");
}

/** Tomorrow, as the picker's `min` — an expiry in the past is rejected server-side. */
export function minExpiryDateInput(): string {
  return dayjs().add(1, "day").format("YYYY-MM-DD");
}

/**
 * The resource types v1 can share, restated on the client because the runtime array lives in a server
 * module and importing it would pull server code into the web bundle.
 *
 * The annotation is the safeguard: it is the union taken from the server's create-request schema, so
 * adding, removing or misspelling an entry here is a compile error rather than a silent divergence.
 */
const SHAREABLE_RESOURCE_TYPES: readonly ExternalLinkShareableResourceType[] = [
  "RESOURCE_TYPE_FILE",
  "RESOURCE_TYPE_FOLDER",
  "RESOURCE_TYPE_MERGED_FILE",
];

/** Folder resource type, so callers never spell the literal. */
export const RESOURCE_TYPE_FOLDER_VALUE: ExternalLinkShareableResourceType = "RESOURCE_TYPE_FOLDER";

/**
 * Narrow a grid row's `resourceType` to something a link may be created on.
 *
 * A real type predicate rather than a cast: estimates, schedules, contracts and transmittal rows all
 * flow through the same file-manager grid, and the server refuses them, so they must be filtered out
 * with the type system rather than assumed away.
 */
export function isShareableResourceType(value: string | undefined): value is ExternalLinkShareableResourceType {
  return !!value && SHAREABLE_RESOURCE_TYPES.some((shareable) => shareable === value);
}

/**
 * "Revoked On" / "Revoked By" for the Revoked tab.
 *
 * A revoked link's creation metadata is not the useful answer on that tab — who killed it and when
 * is. Both fall back to an em dash rather than to the creator, so a row never implies the creator
 * revoked something they did not: `revokedByName` is only absent on rows revoked by an automatic
 * path (a deleted or moved source), where naming any person would be wrong.
 */
export function revokedOnLabel(link: ExternalLinkListItem): string {
  return link.revokedAt ? formatShortDate(link.revokedAt) : EM_DASH;
}

export function revokedByLabel(link: ExternalLinkListItem): string {
  return link.revokedByName ?? link.disabledByName ?? EM_DASH;
}

/** Used wherever a grid cell has no value, so the three link surfaces render the gap identically. */
export const EM_DASH = "—";

/**
 * Build the `ExternalLinkTarget` the dialogs take, validating the ids at the boundary.
 *
 * Returns `null` rather than throwing or casting when anything fails to parse: this runs on grid
 * rows and details-panel selections, which legitimately include estimates, schedules, transmittals
 * and rows whose `spaceId` is only known from the surrounding folder. `objectIdString.safeParse` is
 * what turns those loose strings into the branded ids the server request type demands — the one place
 * that conversion happens, instead of a cast at each call site.
 *
 * Shared by the File Manager context menu and the details-panel section so both decide "can this be
 * shared?" identically.
 */
export function buildExternalLinkTarget(input: {
  resourceId: string | undefined;
  spaceId: string | undefined;
  resourceType: string | undefined;
  name: string;
}): ExternalLinkTarget | null {
  const resourceId = objectIdString.safeParse(String(input.resourceId ?? ""));
  if (!resourceId.success) return null;

  const spaceId = objectIdString.safeParse(String(input.spaceId ?? ""));
  if (!spaceId.success) return null;

  // A predicate, not a cast: the narrowed union is what makes `resourceType` assignable below.
  if (!isShareableResourceType(input.resourceType)) return null;

  return {
    resourceId: resourceId.data,
    spaceId: spaceId.data,
    resourceType: input.resourceType,
    name: input.name,
    isFolder: input.resourceType === RESOURCE_TYPE_FOLDER_VALUE,
  };
}

/**
 * Is external sharing switched on for this portal? (PRD 2.)
 *
 * Reads `metaData.ENABLE_EXTERNAL_LINKS` off the layout payload already in redux, so gating a UI
 * entry point costs no request. Mirrors the server's `isExternalSharingEnabled` exactly, including
 * its default: `metaData` is an open key set and no existing portal document carries the key, so
 * treating "absent" as off would silently disable the feature for every current customer. Only an
 * explicit `false` turns it off.
 *
 * `LayoutData` is a passthrough object, so the undeclared key reads as `unknown` and is narrowed with
 * a `typeof` check — no cast, and a non-boolean value in `metaData` cannot make this throw.
 *
 * Hiding an entry point is presentation only: the server refuses creation and stops resolving links
 * on its own.
 */
export function isExternalSharingEnabled(layout: LayoutData): boolean {
  const flag = layout.ENABLE_EXTERNAL_LINKS;
  return typeof flag === "boolean" ? flag : true;
}

/**
 * Has this expiry already passed?
 *
 * Absent means "never expires", which is not past — the distinction matters because a link with no
 * expiry can be reinstated as-is while one with a lapsed expiry cannot (PRD 3.4).
 */
export function isPastExpiry(expiresAt: number | undefined): boolean {
  return !!expiresAt && expiresAt <= Date.now();
}

/**
 * One shell width for Create / Manage / Stats (PRD 6: "All pop-ups share one fixed shell … so opening a
 * second pop-up from a first never changes the surface size").
 *
 * 61.25rem ≈ the prototype's 980px. The warning confirms (Revoke, Move) deliberately do NOT use this —
 * PRD 6 treats them as a separate, smaller pattern.
 */
export const LINK_DIALOG_WIDTH = "61.25rem";
